import { useMemo, useState } from "react";

import ace from "brace";
import "brace/mode/json";
import "brace/theme/github";
import { JsonEditor } from "jsoneditor-react";
import { debounce } from "lodash";
import {
  Builder,
  Query,
  type ImmutableTree,
} from "react-awesome-query-builder";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useLandingPageAnalyticsContext } from "@features/landing-page-analytics/hooks";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";

import {
  ADVANCED_SEARCH_EDITOR_CONFIG,
  getDefaultRule,
  jsonLogicToSearchTree,
  searchTreeToJsonLogic,
} from "./config";

export const AdvancedSearchEditor = () => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();
  const {
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      dateRange,
      breakdownFilters,
      level,
      adQuery,
      urlQuery,
      advancedSearch,
    },
    setAdvancedSearch,
    setPage,
  } = useLandingPageAnalyticsContext();

  const [matcher, setMatcher] = useState<any>(
    advancedSearch ?? getDefaultRule(),
  );
  const [tree, setTree] = useState(() => jsonLogicToSearchTree(matcher));
  const [isRaw, setIsRaw] = useState(false);

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    level,
    dateRange,
    breakdownFilters,
    adQuery,
    urlQuery,
    advancedSearch,
  };

  const debouncedOnChangeTree = useMemo(
    () =>
      debounce((value: ImmutableTree) => {
        setMatcher(searchTreeToJsonLogic(value));
      }, 500),
    [],
  );

  const debouncedOnChangeRaw = useMemo(
    () =>
      debounce((value: any) => {
        const toSet = value || getDefaultRule();
        try {
          setTree(jsonLogicToSearchTree(toSet));
        } catch (e) {
          return;
        }

        setMatcher(toSet);
      }, 500),
    [],
  );

  const renderBuilder = useMemo(
    () => (props: any) =>
      (
        <div>
          <div className="query-builder-container">
            <div className="query-builder qb-lite ml-0 mr-0">
              <Builder {...props} />
            </div>
          </div>
        </div>
      ),
    [],
  );

  return (
    <div className="border rounded-lg mt-4 pt-2 pl-3 bg-white">
      <div className="flex items-center justify-between mr-3">
        <h4 className="m-0 text-lg font-bold">Advanced Search</h4>
        <div className="flex items-center gap-4">
          {user.isAdmin && (
            <>
              {isRaw ? (
                <SecondaryButton
                  className="font-bold flex items-center gap-2"
                  onClick={() => {
                    prettyLogEventPage({
                      domain: "Landing Page Analytics",
                      action: "onClick",
                      additionalActionInfo:
                        "Change advanced search editor to structured moda",
                    });

                    setIsRaw(false);
                  }}
                >
                  <i className="fa-regular fa-block-quote text-cosmic" />
                  VIEW STRUCTURED
                </SecondaryButton>
              ) : (
                <SecondaryButton
                  className="font-bold flex items-center gap-2"
                  onClick={() => {
                    prettyLogEventPage({
                      domain: "Landing Page Analytics",
                      action: "onClick",
                      additionalActionInfo:
                        "Change advanced search editor to raw moda",
                    });

                    setIsRaw(true);
                  }}
                >
                  <i className="fa-regular fa-code text-cosmic" />
                  VIEW RAW
                </SecondaryButton>
              )}
            </>
          )}
        </div>
      </div>
      <div className="mt-4">
        {isRaw ? (
          <JsonEditor
            value={matcher}
            onChange={debouncedOnChangeRaw}
            mode="code"
            ace={ace}
            htmlElementProps={{ style: { height: 500 } }}
          />
        ) : (
          <div className="react-awesome-query-builder">
            <Query
              {...ADVANCED_SEARCH_EDITOR_CONFIG}
              value={tree}
              onChange={(value) => {
                setTree(value);
                debouncedOnChangeTree(value);
              }}
              renderBuilder={renderBuilder}
            />
          </div>
        )}
      </div>
      <div className="my-4 px-3 flex items-center justify-end">
        <PrimaryButton
          className="font-bold"
          onClick={() => {
            prettyLogEventPage({
              domain: "Landing Page Analytics",
              action: "onChange",
              additionalActionInfo: "Set Advanced search",
              data: {
                newAdvancedSearch: matcher,
                pageContext,
              },
            });

            setPage(1);
            setAdvancedSearch(matcher);
          }}
        >
          SEARCH
        </PrimaryButton>
      </div>
    </div>
  );
};
